import React from "react";
import { Link } from "react-router-dom";
import stats from "../../data/atlasStats.json";
import { REVIEWER, EDITORIAL_PATH } from "../../config/eeat";
import "../../styles/WhyLove.css";

// "What makes Sârv different" — a bento of the reasons that are actually ours.
//
// Every figure here is baked from the database at build time
// (scripts/build-atlas-seed.js), so the section can't quietly become a lie the
// next time the graph is reloaded. Card sizes carry emphasis; that unevenness
// is what stops a list of features reading as a list of features.

// Deterministic thousands separator. Intl/toLocaleString() resolves against the
// host locale, which differs between the Node build machine and the visitor's
// browser — that mismatch would break hydration on the prerendered homepage.
const n = (v) => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const LADDER = [
  { key: "clinical_plant", label: "Clinical trial, whole plant" },
  { key: "clinical_extract", label: "Human data on an extract" },
  { key: "preclinical", label: "Laboratory or animal only" },
  { key: "folk", label: "Traditional use, no science reviewed" },
];

const WhyLoveSection = () => {
  const max = Math.max(...LADDER.map((g) => stats.grades[g.key]));

  return (
    <section className="sv-dark sv-section whylove">
      <div className="sv-wrap">
        <h2 className="sv-h2 sv-center whylove__title">What makes Sârv different</h2>

        <div className="sv-bento">
          {/* --- the thesis, and the only card that gets a visual ---------- */}
          <article className="sv-card sv-span-4 whylove__card">
            <h3 className="sv-h4">Every claim shows its work</h3>
            <p className="sv-body whylove__body">
              {n(stats.edges)} relationships between {n(stats.herbs)} plants,{" "}
              {n(stats.conditions)} conditions and {n(stats.actions)}{" "}
              pharmacological actions — and not one of them is stated without
              the grade of evidence behind it.
            </p>

            <ul className="whylove__ladder">
              {LADDER.map((g) => (
                <li key={g.key} className="whylove__rung">
                  <span className={`whylove__bar whylove__bar--${g.key}`}>
                    <span
                      className="whylove__bar-fill"
                      style={{
                        width: `${Math.max(
                          (stats.grades[g.key] / max) * 100,
                          0.6
                        )}%`,
                      }}
                    />
                  </span>
                  <span className="whylove__rung-label">{g.label}</span>
                  <span className="whylove__rung-count">
                    {n(stats.grades[g.key])}
                  </span>
                </li>
              ))}
            </ul>
          </article>

          {/* --- the honesty differentiator -------------------------------- */}
          <article className="sv-card sv-span-2 whylove__card whylove__card--stat">
            <p className="whylove__figure">{stats.folkShare}%</p>
            <h3 className="sv-h4">is folklore, and we say so</h3>
            <p className="sv-body whylove__body">
              Nearly two thirds of what is written about herbs is traditional
              use with no science reviewed. It isn't worthless — but it isn't
              evidence, and you deserve to know which you're reading.
            </p>
          </article>

          {/* --- safety ------------------------------------------------------
              Absorbs the pregnancy/breastfeeding point that used to have its
              own homepage section. That section was strong but narrow — it
              spoke to a small slice of visitors — so the general form of the
              claim lives here instead: we check, and we tell you when the
              answer is nothing. The per-plant detail belongs on the herb page.
              stats.safety is baked by build-atlas-seed.js. */}
          <article className="sv-card sv-span-2 whylove__card">
            <p className="whylove__figure whylove__figure--sm">
              {stats.unsafeHerbs}
            </p>
            <h3 className="sv-h4">plants you'll never be handed</h3>
            <p className="sv-body whylove__body">
              Herbs rated "do not self-administer" are hidden by default
              everywhere on this site — and every plant is checked for
              pregnancy and breastfeeding, including the{" "}
              {n((stats.safety && stats.safety.noData) || 0)} where the honest
              answer is that nobody knows yet.
            </p>
          </article>

          {/* --- citations --------------------------------------------------- */}
          <article className="sv-card sv-span-2 whylove__card">
            <p className="whylove__figure whylove__figure--sm">
              {stats.sourceRefs}
            </p>
            <h3 className="sv-h4">named references, not vibes</h3>
            <p className="sv-body whylove__body">
              Every relationship traces back to a citation you can look up —
              Commission E, WHO monographs, published trials. No "studies show".
            </p>
          </article>

          {/* --- the map ----------------------------------------------------- */}
          <article className="sv-card sv-span-2 whylove__card">
            <h3 className="sv-h4">One map, not ten thousand articles</h3>
            <p className="sv-body whylove__body">
              Search a plant, a symptom or an action and follow the connections
              outward. The Atlas is the whole corpus in one view —{" "}
              {n(stats.publishedConditions)} conditions have enough behind them
              to stand on their own.
            </p>
            <Link className="whylove__link" to="/atlas">
              Open the Atlas →
            </Link>
          </article>

          {/* --- E-E-A-T ------------------------------------------------------ */}
          <article className="sv-card sv-span-6 whylove__card whylove__card--wide">
            <div>
              <h3 className="sv-h4">
                Reviewed by people, not generated by a machine
              </h3>
              <p className="sv-body whylove__body">
                The knowledge graph is extracted from published pharmacological
                reference works and checked against them. Editorial content is
                reviewed by {REVIEWER.name}, and we publish how we work.
              </p>
            </div>
            <Link className="whylove__link" to={EDITORIAL_PATH}>
              Our editorial process →
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
};

export default WhyLoveSection;
